// AI Upgraded Service
import { Injectable } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { ComplianceService } from '@client/app/blocks/service/compliance/compliance.service';
import { ActiveFeatureService } from '@client/ng2/services/active-feature/active-feature.service';
import { HistoryHelperService } from '@client/app/blocks/helpers/history.helper.service';
import { DatePipe } from '@angular/common';
import { MonNumberPipe } from '@monsido/ng2/core/locale/pipes/mon-number.pipe';

@Injectable({
    providedIn: 'root',
})
export class ExporterAccessibilityParserService {
    constructor (
        private readonly translateService: TranslateService,
        private readonly complianceService: ComplianceService,
        private readonly activeFeatureService: ActiveFeatureService,
        private readonly historyHelperService: HistoryHelperService,
        private readonly datePipe: DatePipe,
        private readonly monNumberPipe: MonNumberPipe,
    ) {}

    parse (data: any[]): any[] {
        if (!data?.length) {
            return [];
        }

        const csv: string[][] = [];
        const allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');

        csv.push(this.getHeadlines(allowVerticalAverage));

        data.forEach((item) => {
            const entry: any[] = [];
            entry.push(this.datePipe.transform(this.translateService.getString(item.post_processing_done_at ?? item.date), 'MMM d, y, h:mm a'));
            entry.push(item.page_count);

            if (item.compliancePercentage != null) {
                entry.push(this.monNumberPipe.transform(item.compliancePercentage / 100, '0.[00]%'));
            } else {
                entry.push(this.getCompliance(item));
            }

            if (allowVerticalAverage) {
                entry.push(item.comparisonToAverage);
            }

            const checks = item.accessibility_checks;

            if (checks) {
                entry.push(checks['A']);
                entry.push(checks['AA']);
                entry.push(checks['AAA']);
            } else {
                entry.push('');
            }

            entry.push(item.accessiblity_issues_count);
            entry.push(this.historyHelperService.getCaretCharacter(item.changeValueIconClass) + ' ' + item.changePercentageValue);

            csv.push(entry);
        });

        return csv;
    }

    private getHeadlines (allowVerticalAverage: boolean): string[] {
        const headlines = [
            this.translateService.getString('Crawled at'),
            this.translateService.getString('Pages crawled'),
            this.translateService.getString('Domain compliance'),
        ];

        if (allowVerticalAverage) {
            headlines.push(this.translateService.getString('Comparison to average'));
        }

        headlines.push(
            this.translateService.getString('Failing checks - A'),
            this.translateService.getString('Failing checks - AA'),
            this.translateService.getString('Failing checks - AAA'),
            this.translateService.getString('Issues'),
            this.translateService.getString('Issues changes since previous crawl'),
        );

        return headlines;
    }

    private getCompliance (item: any): string {
        item.accessibilityComplianceData ??= this.complianceService.accessibilityHistoryCompliance(item);
        return this.monNumberPipe.transform(item.accessibilityComplianceData?.[0] / 100, '0.[00]%') ?? '';
    }
}
